/**
 * Copyright ©2024 Drivepoint
 */

import React, {ElementType} from "react";
import {Button, Link} from "@mui/material";
import {closeSnackbar, enqueueSnackbar, SnackbarKey, SnackbarMessage} from "notistack";
import "./Toast.css";

type Action = {
  label: string;
  onClick: () => void;
  closeToast?: boolean;
};

type ToastErrorInfo = {
  message: string;
  reload?: boolean;
};

type ToastAdditionalOptions = {
  persist?: boolean
};

type SuccessToastAdditionalOptions = {
  OtherActions?: ElementType,
  options?: ToastAdditionalOptions
};

export default class Toast {

  private static _progressIntervalId?: ReturnType<typeof setInterval>;
  private static _progressIntervalKey?: SnackbarKey;

  static error(error: Error, options: ToastAdditionalOptions = {}): void {
    if (!error) { return; }
    const errorInfo = Toast._getErrorInfo(error);
    const action = (snackbarId: SnackbarKey | undefined) => (
      <>
        {errorInfo.reload && <Button color="inherit" onClick={() => { window.location.reload(); }}>Refresh</Button>}
        <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
      </>
    );
    enqueueSnackbar(
      errorInfo.message, {
        action, variant: "error",
        anchorOrigin: {horizontal: "center", vertical: "top"},
        persist: options.persist ?? false
      });
  }

  static errorWithLink(message: string, linkText: string, linkUrl: string, options: ToastAdditionalOptions = {}): void {
    const content = (
      <span className="toast-error-with-link-content">
        {message}{"\u00A0"}<Link href={linkUrl} target="_blank" rel="noopener noreferrer" className="toast-error-with-link">{linkText}</Link>.
      </span>
    );

    const action = (snackbarId: SnackbarKey | undefined) => (
      <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
    );

    enqueueSnackbar(content, {
      action,
      variant: "error",
      anchorOrigin: {horizontal: "center", vertical: "top"},
      persist: options.persist ?? false
    });
  }

  static success(message: string, {OtherActions, options}: SuccessToastAdditionalOptions = {}): void {
    if (!message) { return; }
    const action = (snackbarId: SnackbarKey | undefined) => (
      <>
        {OtherActions && <OtherActions />}
        <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
      </>
    );
    enqueueSnackbar(message, {
      action,
      variant: "success",
      persist: options?.persist ?? false,
      anchorOrigin: {horizontal: "center", vertical: "top"}
    });
  }

  static progress(message: string): SnackbarKey | undefined {
    if (!message) { return; }
    return enqueueSnackbar(message, {persist: true, anchorOrigin: {horizontal: "center", vertical: "top"}});
  }

  static progressWithSteps(messages: string[]) {
    let index = 0;
    this._progressIntervalKey = Toast.progress(messages[index]);
    this._progressIntervalId = setInterval(() => {
      index = (index + 1) % messages.length;
      closeSnackbar(this._progressIntervalKey);
      this._progressIntervalKey = Toast.progress(messages[index]);
    }, 3000);
  }

  static warning(message: SnackbarMessage, actions?: Action[], options: ToastAdditionalOptions = {}) {
    const actionComponent = (snackbarId: SnackbarKey | undefined) => (
      <>
        {this._renderActions(actions, snackbarId)}
        <Button color="inherit" onClick={() => { closeSnackbar(snackbarId); }}>Dismiss</Button>
      </>
    );
    enqueueSnackbar(message, {variant: "warning", anchorOrigin: {horizontal: "center", vertical: "top"}, persist: options.persist ?? false, action: actionComponent});
  }

  static clearProgress() {
    clearInterval(this._progressIntervalId);
    closeSnackbar(this._progressIntervalKey);
  }

  private static _renderActions(actions: Action[] = [], snackbarId: SnackbarKey | undefined) {
    return actions?.map((action, index) => (
      <Button key={index} color="inherit" onClick={() => {
        action.onClick();
        if (action.closeToast) { closeSnackbar(snackbarId); }
      }}>{action.label}</Button>
    ));
  }

  private static _getErrorInfo(error: any): ToastErrorInfo {
    const info: ToastErrorInfo = {message: error.message};
    if (error instanceof TypeError) {
      if (error.message === "Failed to fetch") {
        info.message = "There was a problem connecting to our service, please wait and try again.";
      } else {
        info.message = "An unexpected error occurred, please refresh the page and try again.";
      }
      info.reload = true;
    }
    switch (error.code) {
      case 409:
        info.message = "This item already exists. Please choose a new one";
        break;
      case 404:
        info.message = "The item was not found.";
        break;
      case 423:
        info.message = "The item is currently locked, please try again.";
        break;
      case "423_dnd":
        info.message = "The item could not be moved because a user has it open in the background. Please close the Plan and try again.";
        break;
    }
    return info;
  }

}
